import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

interface TicketTypeData {
  CASE?: { total: number };
  INCIDENT: { total: number };
  SERVICE_REQUEST: { total: number };
  CHANGE: { total: number };
  PROBLEM: { total: number };
}

interface TicketTypePieChartProps {
  data: TicketTypeData;
  onSegmentClick?: (ticketType: string) => void;
}

const TicketTypePieChart: React.FC<TicketTypePieChartProps> = ({ data, onSegmentClick }) => {
  const labels = ['Cases', 'Incidents', 'Service Requests', 'Changes', 'Problems'];
  const values = [
    data.CASE?.total || 0,
    data.INCIDENT.total,
    data.SERVICE_REQUEST.total,
    data.CHANGE.total,
    data.PROBLEM.total
  ];
  const ticketTypes = ['CASE', 'INCIDENT', 'SERVICE_REQUEST', 'CHANGE', 'PROBLEM'];
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: [
          'rgba(147, 51, 234, 0.8)',  // purple - Cases
          'rgba(239, 68, 68, 0.8)',   // red - Incidents
          'rgba(59, 130, 246, 0.8)',  // blue - Service Requests
          'rgba(16, 185, 129, 0.8)',  // green - Changes
          'rgba(245, 158, 11, 0.8)',  // amber - Problems
        ],
        borderColor: [
          'rgb(147, 51, 234)',
          'rgb(239, 68, 68)',
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
        ],
        borderWidth: 2,
        hoverOffset: 10,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 15,
          usePointStyle: true,
          font: { size: 12 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onSegmentClick) {
        const index = elements[0].index;
        onSegmentClick(ticketTypes[index]);
      }
    },
  };

  return (
    <div id="chart-ticket-type" className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Ticket Type Distribution</h3>
      <div className="h-64">
        <Pie data={chartData} options={options} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        {labels.map((label, index) => (
          <div
            key={label}
            className="flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer"
            onClick={() => onSegmentClick?.(ticketTypes[index])}
          >
            <span className="text-gray-600">{label}</span>
            <span className="font-medium">{values[index]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TicketTypePieChart;
